import { useEffect, useState, useMemo } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Flex } from "antd"
import CollapsibleSection from "ui/CollapsibleSection"
import CrewCell, { normalizeStaffList, staffRoleLabel } from "../../../BookingTable/CrewCell"
import ResourceCell from "../../../BookingTable/ResourceCell"
import { getAssignedCrew, getAvailableStaff, selectAssignedCrew } from "store/staffAssignments"
import { fetchAssignedEquipments, selectAssignedEquipments } from "store/equipmentAssignments"
import { BLACKOUT_SCOPES, formatEntry, formatEntryWithTime, isConflictedResource } from "helpers/blackouts"
import styles from "../../index.module.css"

// "crew_member" → "Crew Member". Last-resort label, from the assignment's own enum.
const humanizeRole = (role) =>
  (role || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

// When the stranded resource is unavailable: the blackout's own span from the conflict. The
// booking's date is the fallback for a payload that predates the date fields — the blackout covers
// it by definition.
const toConflictDateLabel = (conflict, bookingDate) => {
  if (!conflict) return ""
  if (conflict.start_date) return formatEntryWithTime(conflict)
  return bookingDate ? formatEntry({ start_date: bookingDate }, true) : ""
}

// GET /guide/bookings/{id}/staff_assignments → the chip shape CrewCell/EditableTagCell expect
// (assignmentId drives the × unassign, profileId matches picker-added chips).
//
// The role shown is the member's ORG role ("Admin", "Crew Member", …) so it reads the same as
// the By Experience panel, which gets `role_label` straight from the experience payload. A
// staff_assignments row has no such field — only `assignment_role`, an enum the frontend always
// posts as "crew_member" — so the label is resolved from the availability listing (`roleLookup`,
// keyed by profile id and by name, since not every payload carries profile_id). If the member
// isn't in that listing we fall back to the humanized assignment role rather than showing no
// role at all.
const toCrewChip = (assignment, roleLookup, conflict, conflictDate) => {
  const name = assignment.profileName || assignment.profile?.fullName || ""
  const profileId = assignment.profileId ?? assignment.profile?.id
  const orgRole = roleLookup.byProfileId.get(profileId) || roleLookup.byName.get(name.trim().toLowerCase())
  return {
    id: profileId ?? assignment.id,
    name,
    role: orgRole || humanizeRole(assignment.assignmentRole),
    assignmentId: assignment.id,
    profileId,
    isUnavailable: isConflictedResource(conflict, BLACKOUT_SCOPES.CREW, name),
    unavailableReason: conflict?.reason || conflict?.label,
    unavailableDate: conflictDate
  }
}

// GET /guide/bookings/{id}/assigned_equipments → chip shape ResourceCell expects. One chip per
// assigned asset, suffixed with the asset number when the equipment has several.
const toEquipmentChip = (equipment, conflict, conflictDate) => {
  const name = equipment.equipment_title || equipment.equipment_name || equipment.name || ""
  const chipName = equipment.asset_number ? `${name} (${equipment.asset_number})` : name
  return {
    id: `${equipment.equipment_id}-${equipment.asset_id}`,
    name: chipName,
    equipmentId: equipment.equipment_id,
    assetId: equipment.asset_id,
    // Both spellings are tried: the conflict names the asset ("Kayak Boat - Asset #0002") while
    // the chip may carry the asset number in brackets instead.
    isUnavailable:
      isConflictedResource(conflict, BLACKOUT_SCOPES.EQUIPMENT, chipName) ||
      isConflictedResource(conflict, BLACKOUT_SCOPES.EQUIPMENT, name),
    unavailableReason: conflict?.reason || conflict?.label,
    unavailableDate: conflictDate
  }
}

// Crew + Equipment for a single reservation. A reservation IS a trip_booking, so the same
// booking-scoped assignment endpoints the By Experience detail uses apply here unchanged —
// only the initial chips have to be fetched, since the reservation detail payload omits them.
const ResourcesPanel = ({ bookingUuid, bookingDate = null, collapsible = false, onAssignmentsChange, conflict = null }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const assignedCrew = useSelector(selectAssignedCrew)
  const assignedEquipments = useSelector(selectAssignedEquipments)
  // Org role per member, from the same availability listing the crew picker uses, so a chip here
  // reads identically to the By Experience panel and to a freshly picked chip. Indexed both ways
  // because the two payloads don't reliably share a profile id.
  const [roleLookup, setRoleLookup] = useState(() => ({ byProfileId: new Map(), byName: new Map() }))

  useEffect(() => {
    if (!bookingUuid) return
    dispatch(getAssignedCrew(bookingUuid))
    dispatch(fetchAssignedEquipments(bookingUuid))
    dispatch(getAvailableStaff(bookingUuid, { filter: "all" }))
      .then((res) => {
        const staffList = normalizeStaffList(res?.data)
        setRoleLookup({
          byProfileId: new Map(staffList.map((staff) => [staff.id, staffRoleLabel(staff)])),
          byName: new Map(
            staffList.map((staff) => [
              (staff.full_name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim()).toLowerCase(),
              staffRoleLabel(staff)
            ])
          )
        })
      })
      .catch(() => {})
  }, [bookingUuid, dispatch])

  const conflictDate = useMemo(() => toConflictDateLabel(conflict, bookingDate), [conflict, bookingDate])
  const initialCrew = useMemo(
    () => (assignedCrew || []).map((assignment) => toCrewChip(assignment, roleLookup, conflict, conflictDate)),
    [assignedCrew, roleLookup, conflict, conflictDate]
  )
  const initialEquipment = useMemo(
    () => (assignedEquipments || []).map((equipment) => toEquipmentChip(equipment, conflict, conflictDate)),
    [assignedEquipments, conflict, conflictDate]
  )

  // The cells own their chips once mounted and mutate them optimistically, so mirror the
  // By Experience panel and track live counts for the header instead of the fetched lengths.
  const [crewCount, setCrewCount] = useState(initialCrew.length)
  const [equipmentCount, setEquipmentCount] = useState(initialEquipment.length)
  useEffect(() => setCrewCount(initialCrew.length), [initialCrew.length])
  useEffect(() => setEquipmentCount(initialEquipment.length), [initialEquipment.length])

  const bodyContent = (
    <Flex vertical gap={12}>
      <Flex vertical gap={4}>
        <span className={styles.resourceGroupLabel}>{t("booking.reservation_detail.crew")}</span>
        <CrewCell
          initialCrew={initialCrew}
          bookingId={bookingUuid}
          emptyLabel={t("booking.reservation_detail.no_crew_assigned")}
          alwaysEditable
          isToggleMode
          onCountChange={setCrewCount}
          onAssignmentsChange={onAssignmentsChange}
        />
      </Flex>
      <Flex vertical gap={4}>
        <span className={styles.resourceGroupLabel}>{t("booking.reservation_detail.equipment")}</span>
        <ResourceCell
          initialResources={initialEquipment}
          bookingId={bookingUuid}
          emptyLabel={t("booking.reservation_detail.no_equipment_assigned")}
          alwaysEditable
          onCountChange={setEquipmentCount}
          onAssignmentsChange={onAssignmentsChange}
        />
      </Flex>
    </Flex>
  )

  if (collapsible) {
    return (
      <CollapsibleSection
        title={t("booking.reservation_detail.resources")}
        count={crewCount + equipmentCount}
        className={styles.commPanel}
      >
        {bodyContent}
      </CollapsibleSection>
    )
  }

  return (
    <Flex vertical gap={12} className={styles.commPanel}>
      <span className={styles.commPanelTitle}>
        {t("booking.reservation_detail.resources")} ({crewCount + equipmentCount})
      </span>
      {bodyContent}
    </Flex>
  )
}

ResourcesPanel.propTypes = {
  bookingUuid: PropTypes.string,
  /** "YYYY-MM-DD" — dates the unavailable tooltip when the conflict carries no dates of its own. */
  bookingDate: PropTypes.string,
  collapsible: PropTypes.bool,
  // Fired after a crew/equipment assign or unassign succeeds.
  onAssignmentsChange: PropTypes.func,
  // The booking's blackout conflict, when it has one — the chip it names is marked unavailable.
  conflict: PropTypes.shape({
    scope: PropTypes.string,
    label: PropTypes.string,
    reason: PropTypes.string,
    resource_name: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string
  })
}

export default ResourcesPanel
